import { useState } from 'react';
import type { RoundMultipliers, RoundStatus } from '@kaeliux/shared';
import { DEATH_MATCH_MULTIPLIER, SUNS_TO_WIN } from '@kaeliux/shared';
import { useNow } from '../hooks/useNow';
import { useRoundStatus } from '../hooks/useRoundStatus';
import { formatCountdown, formatFullNumber } from '../utils/formatters';
import { cn } from '../utils/cn';
import { Button } from './ui/Button';
import { Icon } from './ui/Icon';
import type { Tone } from './ui/toneStyles';

/** Countdown to `until`, never negative — "0:00" once it has passed and the next refresh catches up. */
function countdown(until: Date | undefined, now: number): string {
    return formatCountdown(Math.max(0, (until?.getTime() ?? now) - now));
}

const TONE_CLASSES: Partial<Record<Tone, string>> = {
    danger: 'border-status-danger/40 bg-status-danger/15',
    warning: 'border-status-warning/40 bg-status-warning/15',
    info: 'border-status-info/40 bg-status-info/15',
    success: 'border-status-success/40 bg-status-success/15',
};

function Notice({ tone, testId, children }: { tone: Tone; testId: string; children: React.ReactNode }) {
    return (
        <div
            data-testid={testId}
            role="status"
            className={cn('flex flex-wrap items-center gap-3 rounded-tile border px-4 py-3', TONE_CLASSES[tone])}
        >
            <Icon name="alert" size={18} className="shrink-0 text-content-primary" />
            <div className="min-w-0 flex-1 text-body text-content-primary">{children}</div>
        </div>
    );
}

/** How fast Death Match runs — every multiplier was raised to at least {@link DEATH_MATCH_MULTIPLIER}, and one already faster kept its own. */
function deathMatchPace(multipliers: RoundMultipliers | undefined): string {
    const { resourceMultiplier, speedMultiplier, flightMultiplier } = multipliers ?? {
        resourceMultiplier: DEATH_MATCH_MULTIPLIER,
        speedMultiplier: DEATH_MATCH_MULTIPLIER,
        flightMultiplier: DEATH_MATCH_MULTIPLIER,
    };
    if (resourceMultiplier === speedMultiplier && speedMultiplier === flightMultiplier) {
        return `everything runs at ${formatFullNumber(resourceMultiplier)}×`;
    }
    return `income runs at ${formatFullNumber(resourceMultiplier)}×, construction and research at ${formatFullNumber(speedMultiplier)}× and fleets at ${formatFullNumber(flightMultiplier)}×`;
}

function FinalStandings({ status }: { status: RoundStatus }) {
    if (!status.finalStandings?.length) return null;
    return (
        <ol className="mt-2 flex flex-col gap-1 text-label text-content-secondary" data-testid="round-final-standings">
            {status.finalStandings.map((standing) => (
                <li key={standing.allianceId}>
                    {standing.rank}. <span className="text-content-muted">[{standing.tag}]</span> {standing.name} —{' '}
                    {standing.suns} Sun{standing.suns === 1 ? '' : 's'}, {formatFullNumber(standing.totalPoints)} alliance points
                </li>
            ))}
        </ol>
    );
}

/**
 * The round's public state, under the HUD on every screen: the seven-Sun
 * countdown, the Endgame, the membership freeze, and — once the round is
 * decided — the winner and Death Match. Never names an alliance before it
 * reaches seven Suns. Polls `/getRoundStatus`, which also lets the server
 * evaluate a deadline that has passed.
 */
export default function RoundBanner() {
    const now = useNow();
    const [showStandings, setShowStandings] = useState(false);
    const { data: status } = useRoundStatus();
    if (!status) return null;

    const notices: React.ReactNode[] = [];
    if (status.winner) {
        const winnerName = status.winner.tag ? `[${status.winner.tag}] ${status.winner.name}` : null;
        notices.push(
            <Notice key="winner" tone="success" testId="round-banner-winner">
                <p>
                    {winnerName
                        ? <>Round {status.roundNumber} is over — <span className="font-semibold">{winnerName}</span> won{status.winner.reason === 'sevenSuns' ? ` by holding ${SUNS_TO_WIN} Suns` : ' when the Endgame ran out'}.</>
                        : <>Round {status.roundNumber} is over — no alliance was left to win it.</>}{' '}
                    {status.phase === 'deathMatch'
                        ? <>Death Match: <span data-testid="round-death-match-pace">{deathMatchPace(status.deathMatchMultipliers)}</span> until the universe resets, in {countdown(status.deathMatchEndsAt, now)} at the earliest. Scores no longer count.</>
                        : <>The universe will reset for the next round shortly.</>}
                </p>
                {status.finalStandings?.length ? (
                    <Button type="button" size="sm" variant="ghost" className="mt-1" onClick={() => setShowStandings((v) => !v)}>
                        {showStandings ? 'Hide final standings' : 'Final standings'}
                    </Button>
                ) : null}
                {showStandings && <FinalStandings status={status} />}
            </Notice>,
        );
    }
    if (status.victoryContest) {
        notices.push(
            <Notice key="contest" tone="danger" testId="round-banner-contest">
                <span className="font-semibold">[{status.victoryContest.tag}] {status.victoryContest.name}</span> holds {SUNS_TO_WIN} Suns
                and wins the round in <span className="font-semibold" data-testid="round-contest-countdown">{countdown(status.victoryContest.endsAt, now)}</span>{' '}
                unless one of its Suns is reclaimed. Until then, only its Suns may be attacked.
            </Notice>,
        );
    }
    if (status.phase === 'endgame' && !status.winner) {
        notices.push(
            <Notice key="endgame" tone="warning" testId="round-banner-endgame">
                Endgame{status.endgameReason === 'sunBattles' ? ' — enough Sun battles have been fought' : ' — the round has run its course'}. The round
                ends in <span className="font-semibold" data-testid="round-endgame-countdown">{countdown(status.endgameEndsAt, now)}</span>: most Suns
                wins, then total alliance points.
            </Notice>,
        );
    }
    if (status.membershipFrozen && !status.winner) {
        notices.push(
            <Notice key="frozen" tone="info" testId="round-banner-frozen">
                Alliance membership is frozen for the rest of this round — no one can join or leave an alliance, and no alliance can be founded or disbanded.
            </Notice>,
        );
    }
    if (notices.length === 0) return null;

    return <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 px-4 pt-3" data-testid="round-banner">{notices}</div>;
}
